import { useCallback, useMemo } from 'react'
import { Check, ShieldX, TriangleAlert } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchFraudCases, resolveFraudCase, SEC_STR as S, type PaymentFraudRow } from './securityMoreApi'

/**
 * A18.12 — Случаи мошенничества. LIVE `GET /admin/antifraud/payments`;
 * confirm the fraud or clear (false positive) via `.../payments/:id/{confirm|clear}`.
 */
export function FraudCasesScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<PaymentFraudRow>[]>(
    () => [
      { key: 'user', header: S.user, type: 'text' },
      { key: 'amount', header: S.amount, type: 'money', width: 130 },
      { key: 'method', header: S.method, type: 'text', width: 120 },
      { key: 'reason', header: S.reason, type: 'longtext', minWidth: 200 },
      {
        key: 'status',
        header: S.status,
        type: 'enum',
        width: 140,
        options: [
          { value: 'open', label: S.openLbl, status: 'open' },
          { value: 'confirmed', label: S.confirmed, status: 'blocked' },
          { value: 'cleared', label: S.cleared, status: 'active' },
        ],
      },
      { key: 'created_at', header: S.created, type: 'date', width: 150 },
    ],
    [],
  )

  const resolve = useCallback(
    async (row: PaymentFraudRow, action: 'confirm' | 'clear') => {
      try {
        await resolveFraudCase(row.id, action)
        toast(action === 'confirm' ? S.confirmed : S.cleared, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<PaymentFraudRow>[]>(
    () => [
      {
        id: 'confirm',
        label: S.confirm,
        icon: <ShieldX size={14} />,
        danger: true,
        visible: (r) => r.status === 'open',
        onSelect: (r) => resolve(r, 'confirm'),
      },
      {
        id: 'clear',
        label: S.clear,
        icon: <Check size={14} />,
        visible: (r) => r.status === 'open',
        onSelect: (r) => resolve(r, 'clear'),
      },
    ],
    [resolve],
  )

  const fetcher = useCallback(fetchFraudCases, [])
  return (
    <>
      <PageHeader title={S.fraudCases} screenId="A18.12" />
      <DataTable<PaymentFraudRow>
        screenId="A18.12"
        permission={PERMISSIONS.AUDIT_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        rowActions={rowActions}
        emptyState={{ icon: <TriangleAlert size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
